import type {
  AssignableOwnerDto,
  AssignTripOwnerInput,
  CreateTripInput,
  TransitionTripInput,
  TripDto,
  TripListPage,
  TripListQuery,
  UpdateTripInput,
} from "@tc/shared";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

/** A failed API call, carrying the server's message + HTTP status for the UI to surface. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Trip aggregate calls (TW-3437; owner/worklist additions TW-3440). Plain fetch — no TanStack yet. */
export const tripsApi = {
  /** List trips. `clubId` narrows to one club (back office only — RLS scopes club users). */
  list: (clubId?: string) =>
    req<TripDto[]>(`/trips${clubId ? `?clubId=${encodeURIComponent(clubId)}` : ""}`),
  /**
   * One page of trips for the paginated tables. Passing `page` flips `/trips` into envelope mode
   * (server pagination + sorting + status filter); the full-array `list` above stays for the
   * dashboards/calendar that aggregate over every trip.
   */
  listPage: (query: TripListQuery) => {
    const qs = new URLSearchParams();
    if (query.clubId) qs.set("clubId", query.clubId);
    if (query.page != null) qs.set("page", String(query.page));
    if (query.pageSize != null) qs.set("pageSize", String(query.pageSize));
    if (query.sort) qs.set("sort", query.sort);
    if (query.dir) qs.set("dir", query.dir);
    if (query.status) qs.set("status", query.status);
    if (query.startFrom) qs.set("startFrom", query.startFrom);
    if (query.startTo) qs.set("startTo", query.startTo);
    return req<TripListPage>(`/trips?${qs.toString()}`);
  },
  get: (id: string) => req<TripDto>(`/trips/${id}`),
  /** The pick-list of TC operators a trip can be assigned to (back office only). */
  assignableOwners: () => req<AssignableOwnerDto[]>("/trips/assignable-owners"),
  /** Assign (or clear, with `ownerUserId: null`) a trip's TC operator owner (back office only). */
  assignOwner: (id: string, body: AssignTripOwnerInput) =>
    req<TripDto>(`/trips/${id}/owner`, { method: "PATCH", body: JSON.stringify(body) }),
  create: (body: CreateTripInput) =>
    req<TripDto>("/trips", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateTripInput) =>
    req<TripDto>(`/trips/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  transition: (id: string, body: TransitionTripInput) =>
    req<TripDto>(`/trips/${id}/transition`, { method: "POST", body: JSON.stringify(body) }),
  /** Soft-delete (archive) an Enquiry-stage trip. Resolves on 204. */
  remove: (id: string) => req<void>(`/trips/${id}`, { method: "DELETE" }),
};
